import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@ntconsult/aluma-react';
import { fileService } from '../services/fileService';
import { folderService } from '@features/folders/services/folderService';
import type { File } from '../types';
import type { Folder } from '@features/folders';

interface BulkDeleteItems {
  folders: Folder[];
  files: File[];
}

interface BulkDeleteResult {
  succeeded: number;
  failed: number;
}

/**
 * Apaga vários arquivos/pastas via `Promise.allSettled` — uma falha não aborta
 * as demais. Mostra um único toast final em vez de N (diferente de
 * useDeleteFile/useDeleteFolder, que toast por item).
 */
export function useBulkDelete() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('common');
  const [isDeleting, setIsDeleting] = useState(false);

  async function deleteMany({ folders, files }: BulkDeleteItems): Promise<BulkDeleteResult> {
    setIsDeleting(true);
    try {
      const tasks: Promise<unknown>[] = [
        ...folders.map((f) => folderService.deleteFolder(f.id)),
        ...files.map((f) => fileService.deleteFile(f.id)),
      ];
      const results = await Promise.allSettled(tasks);
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;

      queryClient.invalidateQueries({ queryKey: ['folders'] });
      queryClient.invalidateQueries({ queryKey: ['files'] });

      if (failed === 0) {
        toast.success({
          title: t('bulkDelete.success', { count: succeeded }),
        });
      } else if (succeeded > 0) {
        toast.warning({
          title: t('bulkDelete.partial', { succeeded, failed }),
        });
      } else {
        toast.danger({
          title: t('bulkDelete.failed', { count: failed }),
        });
      }

      return { succeeded, failed };
    } finally {
      setIsDeleting(false);
    }
  }

  return { deleteMany, isDeleting };
}
